import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  MessageSquare, 
  Clock,
  Target,
  Sparkles,
  ArrowRight,
  Apple,
  AlertCircle
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function IPhoneAutomationGuide() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  const benefits = [
    {
      icon: Clock,
      title: isRTL ? "חיסכון בזמן" : "Save Time",
      description: isRTL 
        ? "אין צורך לשלוח הודעה ידנית אחרי כל תשלום"
        : "No need to manually send a message after each payment"
    },
    {
      icon: Target,
      title: isRTL ? "דיוק מקסימלי" : "Maximum Accuracy",
      description: isRTL
        ? "המידע מגיע ישירות מ-Apple Wallet ללא טעויות"
        : "Information comes directly from Apple Wallet without errors"
    },
    {
      icon: Sparkles,
      title: isRTL ? "זיהוי סוחר אוטומטי" : "Automatic Merchant Recognition",
      description: isRTL
        ? "המערכת מזהה את שם העסק ומקטלגת אוטומטית"
        : "System recognizes business name and categorizes automatically"
    }
  ];

  const steps = isRTL ? [
    {
      number: 1,
      title: "פתח Shortcuts → Automation",
      description: "באפליקציית Shortcuts, לחץ על הטאב 'Automation' בתחתית המסך ולחץ על הכפתור '+' ליצירת אוטומציה חדשה."
    },
    {
      number: 2,
      title: "בחר Wallet Trigger",
      description: "חפש 'Wallet' ובחר 'When I tap a Wallet Card or Pass'. תוכל לבחור כרטיסים ספציפיים או את כל הכרטיסים.",
      icon: "💳"
    },
    {
      number: 3,
      title: "הוסף Send Message via WhatsApp",
      description: "לחץ על 'Add Action', חפש 'WhatsApp' ובחר 'Send Message'. לחץ על 'Message', בחר 'Select Variable' ← 'Shortcut Input' והוסף את המשתנים בפורמט הבא: \"Amount ils, Name, merchant: Merchant\"",
      icon: "💬",
      example: "Amount, Name, merchant: Merchant"
    },
    {
      number: 4,
      title: "הגדר נמען → Fingent",
      description: "לחץ על 'Recipients' והוסף את מספר הוואטסאפ של Fingent כנמען.",
      icon: "👤"
    },
    {
      number: 5,
      title: "שנה ל-'Run Immediately'",
      description: "גלול למטה ל-'Run After Confirmation' ושנה ל-'Run Immediately'. זה קריטי - בלי זה האוטומציה לא תעבוד!",
      icon: "⚡",
      critical: true
    }
  ] : [
    {
      number: 1,
      title: "Open Shortcuts → Automation",
      description: "In the Shortcuts app, tap the 'Automation' tab at the bottom and tap the '+' button to create a new automation."
    },
    {
      number: 2,
      title: "Select Wallet Trigger",
      description: "Search for 'Wallet' and select 'When I tap a Wallet Card or Pass'. You can choose specific cards or all cards.",
      icon: "💳"
    },
    {
      number: 3,
      title: "Add Send Message via WhatsApp",
      description: "Tap 'Add Action', search for 'WhatsApp' and select 'Send Message'. Tap 'Message', select 'Select Variable' ← 'Shortcut Input' and add the variables in this format: \"Amount ils, Name, merchant: Merchant\"",
      icon: "💬",
      example: "Amount ils, Name, merchant: Merchant"
    },
    {
      number: 4,
      title: "Set Recipient → Fingent",
      description: "Tap 'Recipients' and add Fingent's WhatsApp number as the recipient.",
      icon: "👤"
    },
    {
      number: 5,
      title: "Change to 'Run Immediately'",
      description: "Scroll down to 'Run After Confirmation' and change to 'Run Immediately'. This is critical - without it, automation won't work!",
      icon: "⚡",
      critical: true
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Apple className="h-7 w-7 text-white" />
            </div>
            {isRTL ? "אוטומציה לאייפון" : "iPhone Automation"}
          </h1>
          <p className="text-muted-foreground mt-2 text-lg">
            {isRTL 
              ? "כל תשלום Apple Pay ישלח אוטומטית לפינג'נט"
              : "Every Apple Pay payment automatically sent to Fingent"}
          </p>
        </div>
      </div>

      {/* Benefits */}
      <Card className="border-2 border-primary/20">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4 bg-primary/5 rounded-lg"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
                  <benefit.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-amber-500" />
            {isRTL ? "5 שלבים פשוטים" : "5 Simple Steps"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border-2 ${
                step.critical 
                  ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20' 
                  : 'border-border bg-muted/30'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                  step.critical ? 'bg-amber-500' : 'bg-primary'
                }`}>
                  {step.number}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {step.icon && <span className="text-2xl">{step.icon}</span>}
                    <h3 className="text-lg font-bold flex-1">{step.title}</h3>
                    {step.critical && (
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-muted-foreground">{step.description}</p>
                  {step.example && (
                    <div className="mt-3 space-y-3">
                      {/* Example Format Box */}
                      <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-xs text-blue-600 dark:text-blue-400 font-semibold mb-1">
                          {isRTL ? "דוגמה לפורמט ההודעה:" : "Message format example:"}
                        </p>
                        <code className="text-sm font-mono text-blue-700 dark:text-blue-300 dir-ltr block">
                          {step.example}
                        </code>
                      </div>
                      
                      {/* Visual Example Image */}
                      <div className="p-3 bg-muted/50 rounded-lg border">
                        <p className="text-xs text-muted-foreground font-semibold mb-2">
                          {isRTL ? "כך זה נראה ב-Shortcuts:" : "How it looks in Shortcuts:"}
                        </p>
                        <img 
                          src="/shortcuts-message-example.png" 
                          alt={isRTL ? "דוגמה להגדרת ההודעה" : "Message configuration example"}
                          className="rounded-lg shadow-md w-full max-w-xs mx-auto"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {step.critical && (
                    <p className="mt-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
                      {isRTL 
                        ? "⚠️ ללא שינוי זה, האוטומציה לא תעבוד אוטומטית!"
                        : "⚠️ Without this change, automation won't run automatically!"}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-blue-600" />
            {isRTL ? "טיפים חשובים" : "Important Tips"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                {isRTL
                  ? "וודא שיש לך חיבור אינטרנט ו-WhatsApp מחובר"
                  : "Ensure you have internet connection and WhatsApp is connected"}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                {isRTL
                  ? "תוכל לערוך את האוטומציה בכל עת מאפליקציית Shortcuts"
                  : "You can edit the automation anytime from the Shortcuts app"}
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm">
                {isRTL
                  ? "אחרי ההגדרה, בצע תשלום ראשון עם Apple Pay כדי לבדוק שהכל עובד"
                  : "After setup, make a test payment with Apple Pay to verify everything works"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Success Card */}
      <Card className="border-2 border-green-200 dark:border-green-800 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
            <h2 className="text-2xl font-bold">
              {isRTL ? "מעולה! עכשיו תתחיל לחסוך זמן" : "Great! Now You'll Save Time"}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isRTL
                ? "כל תשלום שתעשה עם Apple Pay יירשם אוטומטית בפינג'נט. לא צריך לעשות כלום!"
                : "Every payment you make with Apple Pay will be automatically recorded in Fingent. Nothing more to do!"}
            </p>
            <div className="flex gap-3 justify-center">
              <Button size="lg" onClick={() => navigate('/whatsapp-config')} className="gap-2">
                <MessageSquare className="h-5 w-5" />
                {isRTL ? "חזרה ל-WhatsApp Config" : "Back to WhatsApp Config"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
